import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/axios';
import Dropdown from './Dropdown';
import CModal from './shared/c-modal';
import { DayAmendments, DayTimeline, DayTotals } from './attendance/daySections';

// Mirrors LOCATIONS in server/constants/attendance.js, which is what the
// attendance_days.override_location CHECK constraint is built from. A value the
// ingest can write but this list omits renders as a blank status field that
// cannot be set back - which is how 'Holiday' behaved after it was added
// server-side. Keep the two in step.
const LOCATIONS = ['Home', 'Office', 'OOO', 'Unknown', 'Absent', 'Holiday'];

/**
 * Correct one attendance cell. Every save is audited server-side, and the
 * correction survives every subsequent ingest run, so the reason field is
 * mandatory rather than a nicety.
 */
// The deadline is evaluated in company time server-side, so the time shown here
// must be too. Rendering it in the viewer's zone would contradict the (late)
// badge sitting next to it.
const formatSigninTime = (iso, timeZone) => {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', timeZone: timeZone || 'UTC',
  });
};

const formatDecisionDate = (iso, timeZone) => {
  if (!iso) return 'an earlier day';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', timeZone: timeZone || 'UTC',
  });
};

const AttendanceOverrideModal = ({ cell, timeZone, canViewDay, onClose, onSaved }) => {
  const [location, setLocation] = useState(cell.location || 'Office');
  const [isLate, setIsLate] = useState(Boolean(cell.is_late));
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [day, setDay] = useState(null);
  const [dayLoading, setDayLoading] = useState(Boolean(canViewDay));

  // The same day the approvals View opens. A Slack-only employee has no
  // timesheet day at all, so a 404 here is a normal answer, not a failure.
  useEffect(() => {
    if (!canViewDay) return undefined;
    let alive = true;
    api.get(`/api/timesheet/days/${cell.date}`, { params: { employee_id: cell.employeeId } })
      .then((res) => { if (alive) setDay(res.data.data); })
      .catch(() => {})
      .finally(() => { if (alive) setDayLoading(false); });
    return () => { alive = false; };
  }, [cell.date, cell.employeeId, canViewDay]);

  const dayZone = (day && day.timezone) || timeZone;
  const dayHasContent = Boolean(day && (day.in_at || day.events?.length || day.amendments?.length));
  // One decision carries both refusals: a from-home request, or an offline office time-in's confirmation.
  let refusal = null;
  if (day && day.wfh_approval === 'rejected') {
    refusal = { testId: 'wfh-rejected', title: 'WFH rejected' };
  } else if (day && day.office_confirmation === 'rejected') {
    refusal = { testId: 'office-unconfirmed', title: 'Office time-in not confirmed' };
  }

  const submit = async (clearing) => {
    if (!reason.trim()) {
      setError('A reason is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = clearing
        ? { location: null, reason: reason.trim() }
        : { location, is_late: isLate, reason: reason.trim() };
      await api.put(`/api/attendance/${cell.id}/override`, payload);
      toast.success(clearing ? 'Correction cleared' : 'Attendance corrected');
      onSaved();
    } catch (err) {
      const message = (err.response && err.response.data && err.response.data.message)
        || 'Could not save the correction.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <CModal
      onClose={onClose}
      labelledBy="override-title"
      dismissible={!saving}
      closeOnBackdrop={false}
      overlayClassName="bg-black/40"
      className="w-full max-w-lg rounded-lg bg-white shadow-xl"
    >
      <div className="flex items-start justify-between border-b border-gray-200 p-4">
        <div>
          <h2 id="override-title" className="text-base font-semibold text-gray-900">Correct attendance</h2>
          <p className="mt-1 text-sm text-gray-500">{cell.employeeName} on {cell.date}</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div className="max-h-[70vh] space-y-4 overflow-y-auto p-4">
        <div className="rounded-md bg-gray-50 p-3 text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <p className="font-medium text-gray-700">Posted in Slack</p>
            {cell.first_signin_at ? (
              <p data-testid="signin-time" className="text-xs text-gray-600">
                {cell.declared_start_at ? 'posted' : 'first signed in'}
                {' '}
                <span className="font-medium text-gray-900">
                  {formatSigninTime(cell.first_signin_at, timeZone)}
                </span>
                {/* An advance notice: the post time is not the start time, so
                    show both or the (late) badge looks like a contradiction. */}
                {cell.declared_start_at && (
                  <>
                    {', announced start '}
                    <span className="font-medium text-gray-900">
                      {formatSigninTime(cell.declared_start_at, timeZone)}
                    </span>
                  </>
                )}
                {timeZone ? ` (${timeZone})` : ''}
              </p>
            ) : (
              <p data-testid="signin-time" className="text-xs italic text-gray-400">no sign-in</p>
            )}
          </div>
          <p className="mt-1 text-gray-600">
            {cell.signin_text || <span className="italic text-gray-400">Nothing posted that day</span>}
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Currently showing: {cell.cell}
            {cell.override_reason ? '' : null}
          </p>
        </div>

        {dayLoading && <p className="text-sm text-gray-500">Loading day...</p>}

        {dayHasContent && (
          <div className="space-y-4 text-sm">
            <DayTotals day={day} timeZone={dayZone} />

            {/* A refused day looks like an accepted one in the grid without
                this, so say who refused it and why. */}
            {refusal && (
              <div data-testid={refusal.testId} className="rounded-md bg-orange-50 p-3 text-orange-900">
                <p className="font-medium">
                  {refusal.title} by {day.wfh_decided_by_name || 'a manager'}
                  {' on '}
                  {formatDecisionDate(day.wfh_decided_at, dayZone)}
                </p>
                {day.wfh_reason && <p className="mt-1 text-xs text-orange-800">{day.wfh_reason}</p>}
              </div>
            )}

            <DayTimeline events={day.events} timeZone={dayZone} />
            <DayAmendments amendments={day.amendments} timeZone={dayZone} />
          </div>
        )}

        {cell.overridden && (
          <p className="rounded-md bg-amber-50 p-3 text-xs text-amber-800">
            Already corrected{cell.override_reason ? `: ${cell.override_reason}` : ''}.
          </p>
        )}

        <div>
          <label htmlFor="override-location" className="block text-sm font-medium text-gray-700">
            Corrected status
          </label>
          <Dropdown
            id="override-location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="mt-1"
          >
            {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
          </Dropdown>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={isLate}
            onChange={(e) => setIsLate(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          Mark as late
        </label>

        <div>
          <label htmlFor="override-reason" className="block text-sm font-medium text-gray-700">Reason</label>
          <textarea
            id="override-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm"
            placeholder="Why is this being changed? Recorded in the audit trail."
          />
        </div>

        {error && <p className="text-sm text-red-700">{error}</p>}
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-gray-200 p-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        {cell.overridden && (
          <button
            type="button"
            disabled={saving}
            onClick={() => submit(true)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Clear correction
          </button>
        )}
        <button
          type="button"
          disabled={saving}
          onClick={() => submit(false)}
          className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </CModal>
  );
};

export default AttendanceOverrideModal;
